/**
 * cmd-pan P2-1 — focus gate for the capture-phase PAN-exit key handler.
 *
 * The capture-phase window keydown listener that ends a realtime PAN on
 * Enter/Escape MUST NOT swallow those keys when focus is in an editable
 * control (command-line input, any `<input>`/`<textarea>`/`<select>` or
 * contenteditable) OR inside a modal dialog. Otherwise typing `LINE`↵ in
 * the command line while a pan is active would have its Enter consumed by
 * the pan-exit handler (`stopImmediatePropagation`) BEFORE the input's own
 * onKeyDown can submit the command — the first Enter would only end PAN and
 * leave the text unsubmitted (same for Escape in inputs / dialogs). When
 * focus is on the canvas / stage / body, the handler runs normally; the
 * App-level command-start teardown handles superseding the pan instead.
 *
 * This is INTENTIONALLY broader than `isEditableTarget` (sysvarHotkeys /
 * useCommandLine) — it additionally bails inside `role="dialog"` AND
 * `role="menu"` subtrees, which those helpers do not cover.
 *
 * cmd-pan r6 P3-3 — the `role="menu"` bail covers the stage shortcut menu
 * (`StageContextMenu`). When a PAN session is active AND that popup is open,
 * its Esc/Enter must reach the menu's OWN keydown listener (Esc → close) — the
 * capture-phase pan-exit handler's `stopImmediatePropagation` would otherwise
 * swallow the key and leave the menu stuck open. Same `closest()` approach as
 * the dialog bail.
 *
 * cmd-pan r7 P2-1 — the r6 `role="menu"` bail only fires when the key TARGET /
 * activeElement is INSIDE the menu. But `StageContextMenu` does NOT focus its
 * `role="menu"` root when it opens from the canvas, so Esc still targets the
 * canvas/body — the focus-scoped bail above misses it, and the capture-phase
 * pan-exit handler `stopImmediatePropagation`s before the menu's own window
 * keydown listener can close it: the first Esc exits PAN but leaves the menu
 * stuck open. `isStageContextMenuOpen` is the focus-INDEPENDENT companion check:
 * if the stage context menu exists ANYWHERE in the document, let its Esc handler
 * run (don't swallow). It is scoped to the menu's stable `data-testid`
 * (`stage-context-menu`) — NOT a bare `[role="menu"]` — so unrelated `role=menu`
 * widgets elsewhere on the page never suppress the pan-exit key.
 */

/** The stable selector for the open stage shortcut menu (`StageContextMenu`). */
const STAGE_CONTEXT_MENU_SELECTOR = '[data-testid="stage-context-menu"]';

/**
 * cmd-pan r7 P2-1 — true when the stage shortcut menu (`StageContextMenu`) is
 * mounted in the document, REGARDLESS of where focus is. The menu does not focus
 * itself on open, so an Esc from the canvas/body must still defer to the menu's
 * own Esc-to-close handler instead of being swallowed by the pan-exit handler.
 * Scoped to the menu's stable testid so unrelated `[role="menu"]` widgets don't
 * trigger the bail.
 */
export function isStageContextMenuOpen(doc: Document = document): boolean {
  return doc.querySelector(STAGE_CONTEXT_MENU_SELECTOR) !== null;
}

export function isPanExitKeyGuarded(
  target: EventTarget | null,
  activeElement: Element | null,
): boolean {
  // cmd-pan r7 P2-1 — focus-INDEPENDENT bail: if the stage shortcut menu is open
  // anywhere, let its own Esc handler close it (the menu does not focus itself,
  // so the focus-scoped `[role="menu"]` bail below would miss a canvas-targeted
  // Esc). Defense in depth — the target/activeElement guards below stay too.
  if (isStageContextMenuOpen()) return true;
  const el =
    target instanceof Element
      ? target
      : activeElement instanceof Element
        ? activeElement
        : null;
  if (el === null) return false;
  if (el instanceof HTMLElement) {
    if (el.isContentEditable) return true;
    if (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return true;
  }
  // Inside an open modal dialog (every dialog shell marks `role="dialog"`):
  // its own keydown handling owns Enter/Escape (confirm / cancel), so the
  // pan-exit capture handler must not intercept them.
  if (el.closest('[role="dialog"]') !== null) return true;
  // cmd-pan r6 P3-3 — inside an open shortcut menu (`StageContextMenu` marks
  // `role="menu"`): its own keydown owns Esc (close), so the pan-exit handler
  // must not swallow it via `stopImmediatePropagation` and leave the menu stuck
  // open. Same `closest()` bail as the dialog subtree above. (r7 P2-1 added the
  // focus-independent `isStageContextMenuOpen` check at the top for the common
  // case where the menu opens without focusing itself.)
  if (el.closest('[role="menu"]') !== null) return true;
  return false;
}
